import { memo, useEffect, useRef } from "react";
import clsx from "clsx";
import type { useForm } from "hooks";
import { Checkbox } from "ui/forms/formik-elements";
import { FormField, type OnboardingBusinessInformation } from "./types";

type Props = Pick<
    ReturnType<typeof useForm<OnboardingBusinessInformation>>,
    | 'setFieldValue'
    | 'setFieldTouched'
    | 'handleBlur'
    | 'values'
    | 'errors'
> & {
    readonly name:
    | FormField.UseContactDeatilsForTech
    | FormField.UseContactDeatilsForFin
    | FormField.UseContactDeatilsForEmergency;
    readonly targetFields: Array<keyof OnboardingBusinessInformation>;
    readonly sourceFields?: Array<keyof OnboardingBusinessInformation>;
};

const UseCompanyContactDetails = ({
    values,
    errors,
    name,
    targetFields,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    sourceFields = [
        FormField.ContactName,
        FormField.ContactEmail,
        FormField.ContactPhone
    ]
}: Props) => {
    const checked = values[name];
    const sourceFieldValue = sourceFields
        .map(field => values[field])
        .join();

    const repository = {
        calculateAndSet: (value: boolean) => {
            sourceFields.forEach(sourceField => {
                const targetField = targetFields[sourceFields.indexOf(sourceField)];

                setFieldValue(
                    targetField,
                    clsx(value && values[sourceField])
                );

                setTimeout(() => setFieldTouched(targetField, true, true));
            });
        }
    };
    const repositoryRef = useRef(repository);
    repositoryRef.current = repository;

    const handleFieldValueChange = (name: string, value: boolean, shouldValidate?: boolean) => {
        setFieldValue(name, value, shouldValidate);

        repositoryRef.current.calculateAndSet(value);
    };

    const isSourceFieldsValid = sourceFields.every(field => Boolean(
        values[field] &&
        !errors[field]
    ));

    useEffect(() => {
        if (!checked) {
            return;
        }

        repositoryRef.current.calculateAndSet(true);
    }, [
        sourceFieldValue,
        checked
    ]);

    return (
        <Checkbox
            setFieldValue={handleFieldValueChange}
            setFieldTouched={setFieldTouched}
            label='Use company contact details'
            handleBlur={handleBlur}
            CheckboxProps={{
                inputProps: {
                    'aria-label': 'Use company contact details'
                },
                name,
                checked,
                disabled: !isSourceFieldsValid
            }}
        />
    );
};

export default memo(UseCompanyContactDetails);
